import React, { useState, useEffect, useMemo, MutableRefObject } from 'react'
import * as PDF from 'react-pdf'
import styled from 'styled-components'
import _ from 'lodash'
import { OcrRegions } from './ocrRegions'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { getRotation } from './common'

if (PDF && PDF.pdfjs && PDF.pdfjs.GlobalWorkerOptions) {
  PDF.pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${PDF.pdfjs.version}/build/pdf.worker.min.mjs`
}

type PDFViewerProps = {
  src: string
  rotation: number
  ocrData: any
  ocrEnabled: boolean
  handleExtractionValueClicked: (event: Event) => void
  ocrSearchText: string
  currentZoomLevel: number
  attachmentIndex: number
  hasMultipleAttachments?: boolean
  projectLocale: string
}

export const PDFViewer = React.forwardRef<HTMLCanvasElement, PDFViewerProps>(
  (
    {
      src,
      rotation = 0,
      ocrData,
      ocrEnabled,
      handleExtractionValueClicked,
      ocrSearchText = '',
      currentZoomLevel = 1,
      attachmentIndex = 1,
      hasMultipleAttachments = false,
      projectLocale
    },
    ref
  ) => {
    const [pageHeight, setPageHeight] = useState<number>()

    const [numPages, setNumPages] = useState(0)
    function onDocumentLoadSuccess({ numPages }) {
      setNumPages(numPages)
    }

    const DocumentOptions = useMemo(
      () => ({
        wasmUrl: `https://unpkg.com/pdfjs-dist@${PDF.pdfjs.version}/wasm/`
      }),
      []
    )

    useEffect(() => {
      const container = document.querySelector('.Layouts__ContentContainer')
      const containerHeight = container?.clientHeight
      const startingHeight = containerHeight
        ? containerHeight - (hasMultipleAttachments ? 128 : 54)
        : 595
      setPageHeight(startingHeight * currentZoomLevel)
    }, [currentZoomLevel, hasMultipleAttachments])

    const file = useMemo(
      () => ({
        url: src
      }),
      [src]
    )

    return (
      <>
        {numPages < 1 && <ScreenSpin />}
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          inputRef={newRef =>
            ((ref as MutableRefObject<HTMLDivElement | null>).current = newRef)
          }
          rotate={getRotation(rotation)}
          $loading={numPages < 1}
          options={DocumentOptions}
        >
          {numPages > 0 &&
            _.range(1, numPages + 1).map(pageNumber => (
              <PDFPage
                key={pageNumber}
                pageNumber={pageNumber}
                totalPageCount={numPages}
                attachmentIndex={attachmentIndex}
                pageHeight={pageHeight}
                ocrData={ocrData}
                ref={ref}
                rotation={rotation}
                ocrEnabled={ocrEnabled}
                handleExtractionValueClicked={handleExtractionValueClicked}
                ocrSearchText={ocrSearchText}
                projectLocale={projectLocale}
              />
            ))}
        </Document>
      </>
    )
  }
)

type PDFPageProps = {
  pageNumber: number
  totalPageCount: number
  attachmentIndex: number
  pageHeight?: number
  ocrData: any
  rotation: number
  ocrEnabled: boolean
  handleExtractionValueClicked: (event: Event) => void
  ocrSearchText: string
  projectLocale: string | null
}

const PDFPage = React.forwardRef<HTMLCanvasElement, PDFPageProps>(
  (
    {
      pageNumber,
      totalPageCount,
      attachmentIndex,
      pageHeight,
      ocrData,
      rotation,
      ocrEnabled,
      handleExtractionValueClicked,
      ocrSearchText = '',
      projectLocale
    },
    ref
  ) => {
    const [width, setWidth] = useState(0)
    const [height, setHeight] = useState(0)
    const [rotate, setRotate] = useState(0)

    function normalizeAngle(angle) {
      return ((angle % 360) + 360) % 360
    }

    const adjustedRotation = useMemo(() => {
      if (!ocrData) return rotation

      const { angle: ocrRotation } = ocrData
      const roundedOcrRotation = ocrRotation[pageNumber - 1]
        ? Math.round(ocrRotation[pageNumber - 1] / 90) * 90
        : 0

      return roundedOcrRotation !== undefined
        ? rotation + rotate - roundedOcrRotation
        : rotation + rotate
    }, [ocrData, pageNumber, rotate, rotation])

    const isRotated = [270, 90].includes(normalizeAngle(adjustedRotation))

    const calculateOCRProps = pdfProps => {
      // pdfProps has a view object with 4 properties; height, width cannot be trusted.
      // If PDF Rotate prop is 90 or 270 rotate OCR boxes and flip width and height values

      // Scale based on pageHeight using width or height based on rotation
      const scaleRatio = !pageHeight
        ? 1
        : !isRotated
          ? pageHeight / (pdfProps.view[3] - pdfProps.view[1])
          : pageHeight / (pdfProps.view[2] - pdfProps.view[0])

      if (pdfProps.rotate === 90 || pdfProps.rotate === 270) {
        setWidth((pdfProps.view[3] - pdfProps.view[1]) * scaleRatio)
        setHeight((pdfProps.view[2] - pdfProps.view[0]) * scaleRatio)
        setRotate(pdfProps.rotate)
      } else {
        setWidth((pdfProps.view[2] - pdfProps.view[0]) * scaleRatio)
        setHeight((pdfProps.view[3] - pdfProps.view[1]) * scaleRatio)
        setRotate(pdfProps.rotate)
      }
    }

    return (
      <StyledPDFPage
        pageNumber={pageNumber}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        rotate={adjustedRotation}
        height={pageHeight}
        onRenderSuccess={pageObject => {
          calculateOCRProps({
            rotate: pageObject.rotate,
            view: pageObject.view
          })
        }}
      >
        {ocrData &&
          ocrData.subRegions.length > 0 &&
          (ref as MutableRefObject<HTMLCanvasElement | null>).current && (
            <OcrRegions
              type="pdf"
              ocrData={ocrData}
              rotation={adjustedRotation - rotate}
              isRotated={isRotated}
              ocrEnabled={ocrEnabled}
              handleExtractionValueClicked={handleExtractionValueClicked}
              pageNumber={pageNumber}
              attachmentIndex={attachmentIndex}
              width={width}
              height={height}
              ocrSearchText={ocrSearchText}
              projectLocale={projectLocale}
            ></OcrRegions>
          )}
        {totalPageCount > 1 && (
          <PageNumber>
            <span>Page:</span> {pageNumber}
          </PageNumber>
        )}
      </StyledPDFPage>
    )
  }
)

const Document = styled(PDF.Document)<{ $loading: boolean }>`
  max-height: 100%;
  width: 100%;
  display: ${({ $loading }) => ($loading ? 'none' : 'flex')};
  flex-direction: column;
`
const StyledPDFPage = styled(PDF.Page)`
  position: relative;
  margin-bottom: 1rem;

  > canvas {
    margin: 0 auto;
  }
`
const PageNumber = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.25rem;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 1rem;

  > span {
    font-size: 0.875rem;
  }
`
